import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { getFromOpenElisServer } from "../utils/Utils";
import {
  serverPageArrowsProps,
  serverPageSizeOf,
  serverPaginationProps,
} from "../utils/serverPaging";
import ServerPageArrows from "../common/ServerPageArrows";
import {
  Tile,
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
  Grid,
  Column,
  Search,
  Heading,
  Section,
} from "@carbon/react";

import "./programCaseView.css";
import PageBreadCrumb from "../common/PageBreadCrumb";
import { FormattedMessage, useIntl } from "react-intl";
import AsyncAvatar from "../patient/photoManagement/photoAvatar/AyncAvatar";

let breadcrumbs = [
  { label: "home.label", link: "/" },
  {
    label: "banner.menu.results.order.programmes",
    link: "/genericProgram",
  },
];

const ProgramDashboard = () => {
  const programDashboardUrl = "/rest/programSamplesList";

  const [summary, setSummary] = useState({
    totalEntries: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [tableRows, setTableRows] = useState([]);
  // The rows a full server page holds, read off the responses; Carbon's items
  // per page is pinned to it so Carbon's page is the server's page.
  const [serverPageSize, setServerPageSize] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const intl = useIntl();
  // Identifies the load in flight, so a late answer never replaces a newer page.
  const latestRequest = useRef(0);

  /** Applies one server page and its page announcement to the table. */
  const applyPage = (response) => {
    if (!response || !response.orderProgramsDashboardForm) return;

    const form = response.orderProgramsDashboardForm;

    const paging = form.paging || {};

    const totalPages = Number(paging.totalPages || 1);
    const currentPage = Number(paging.currentPage || 1);

    setSummary({
      totalEntries: response.totalEntries || 0,
      totalPages,
      currentPage,
    });

    const formatted = form.orderPrograms.map((item) => ({
      id: String(item.programSampleId),
      patientId: item.patientPK,
      patientName: `${item.firstName || ""} ${item.lastName || ""}`,
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      programName: item.programName || "",
      programCode: item.programCode || "",
      accession: item.accessionNumber || "",
      receivedDate: new Date(item.receivedDate).toLocaleDateString() || "",
      questionnaireResponseUuid: item.questionnaireResponseUuid || "",
    }));

    setTableRows(formatted);
    setServerPageSize((previous) =>
      serverPageSizeOf(paging, formatted.length, previous),
    );
  };

  /** A new search: the server caches the matching list and answers with page 1. */
  const fetchDashBoard = (filter) => {
    const url = filter
      ? `${programDashboardUrl}?filter=${encodeURIComponent(filter)}`
      : programDashboardUrl;
    const requestId = ++latestRequest.current;
    getFromOpenElisServer(url, (response) => {
      if (requestId === latestRequest.current) applyPage(response);
    });
  };

  /** One server page of the last search, the same request for the arrows and for Carbon. */
  const loadPage = (pageNumber) => {
    const requestId = ++latestRequest.current;
    getFromOpenElisServer(
      `${programDashboardUrl}?page=${pageNumber}`,
      (response) => {
        if (requestId === latestRequest.current) applyPage(response);
      },
    );
  };

  const history = useHistory();

  const arrows = serverPageArrowsProps({
    paging: summary,
    onPageRequest: loadPage,
  });

  const handleRowClick = (programSampleId) => {
    history.push(`/programView/${programSampleId}`);
  };

  useEffect(() => {
    fetchDashBoard(searchTerm);
  }, [searchTerm]);

  const headers = [
    { key: "avatar", header: "" }, // avatar column
    { key: "firstName", header: <FormattedMessage id="eorder.name.first" /> },
    { key: "lastName", header: <FormattedMessage id="eorder.name.last" /> },
    {
      key: "programName",
      header: <FormattedMessage id="program.name.label" />,
    },
    { key: "programCode", header: <FormattedMessage id="storage.room.code" /> },
    {
      key: "accession",
      header: (
        <FormattedMessage
          id="barcode.label.info.labNumber"
          defaultMessage={intl.formatMessage({
            id: "barcode.label.info.labnumber",
          })}
        />
      ),
    },
    {
      key: "receivedDate",
      header: <FormattedMessage id="label.audittrailreport.receiveddate" />,
    },
    {
      key: "questionnaireResponseUuid",
      header: <FormattedMessage id="notebook.label.questionnaire" />,
    },
  ];

  const displayedRows = tableRows;

  const tileList = [
    {
      title: <FormattedMessage id="notebook.label.total" />,
      count: summary.totalEntries,
    },
  ];

  return (
    <>
      <PageBreadCrumb breadcrumbs={breadcrumbs} />
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <Section>
            <Heading>
              <FormattedMessage
                id="banner.menu.results.order.programmes"
                defaultMessage="Order Programmes"
              />
            </Heading>
          </Section>
        </Column>
        <Column sm={4} md={8} lg={16} className="dashboard-containar">
          {tileList.map((tile, idx) => (
            <Tile className="dashboard-div" key={idx}>
              <h3 className="tile-title-Programe">{tile.title}</h3>
              <p className="tile-value">{tile.count}</p>
            </Tile>
          ))}
        </Column>

        <Column sm={4} md={8} lg={16} className="table-container">
          <div className="table-item">
            <Search
              size="lg"
              labelText={intl.formatMessage({
                id: "sampleManagement.search.label",
              })}
              placeholder={intl.formatMessage({
                id: "sampleManagement.search.label",
              })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {arrows.show && <ServerPageArrows {...arrows} />}
            <DataTable rows={displayedRows} headers={headers} isSortable>
              {({ headers, getHeaderProps, getTableProps }) => (
                <>
                  <TableContainer>
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map((header) => (
                            <TableHeader
                              key={header.key}
                              {...getHeaderProps({ header })}
                            >
                              {header.header}
                            </TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {displayedRows.map((row) => (
                          <TableRow
                            key={row.id}
                            onClick={() => handleRowClick(row.id)}
                            style={{ cursor: "pointer", padding: "0.5rem" }}
                          >
                            {headers.map((header) => (
                              <TableCell key={header.key}>
                                {header.key === "avatar" ? (
                                  <AsyncAvatar
                                    patientId={row.patientId}
                                    patientName={row.patientName}
                                    hasPhoto={true}
                                    size={40}
                                  />
                                ) : (
                                  row[header.key]
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Pagination
                    {...serverPaginationProps({
                      paging: summary,
                      rowsOnPage: tableRows.length,
                      pageSize: serverPageSize,
                      onPageRequest: loadPage,
                      intl,
                    })}
                  />
                </>
              )}
            </DataTable>
          </div>
        </Column>
      </Grid>
    </>
  );
};

export default ProgramDashboard;
